<template>
  <v-card>
    <v-card-title>
      <div
        style="width: 100%;"
      >
        <alert
          v-if="isQuickEntry"
          color="primary"
        >
          Quick entry is only applicable to wound care practice types and requires a date of service set to the current date.
        </alert>
      </div>
      <h3>{{ title }}</h3>
      <v-spacer></v-spacer>
      <patient-name
        class="mt-2"
        :patient="patient"
      ></patient-name>
    </v-card-title>
    <v-card-subtitle
      class="pb-0"
      style="text-align: right;"
    >
      <patient-info
        :patient="patient"
      ></patient-info>
    </v-card-subtitle>
    <patient-insurance-banner
      :patient-id="patient.id"
    />
    <v-card-text>
      <v-form
        ref="createEncounterForm"
        v-model="formValid"
        :disabled="!baseDataLoaded"
      >
        <auto-complete-box
          v-model="encounter.practice_type_id"
          :items="practiceTypeTemplates"
          label="Practice Type"
          class="mt-5"
          required
          :disabled="isQuickEntry"
          @change="$nextTick(() => {selectVisitType(previousEncounter)})"
        >
        </auto-complete-box>
        <auto-complete-box
          v-model="encounter.place_of_service_id"
          :items="facilities"
          label="Facility"
          required
        >
        </auto-complete-box>
        <date-picker
          v-model="encounter.visit_date"
          :disabled="isQuickEntry"
          :min="getMinDate"
          :max="$date().format('YYYY-MM-DD')"
          label="Visit Date"
          required
          @input="$nextTick(() => {selectVisitType(previousEncounter)})"
        ></date-picker>
        <radio-buttons
          v-model="encounter.visit_type"
          :items="visitTypes"
          label="Visit Type"
          columns="4"
          disabled
        >
          <help-icon
            title="Visit Type Automatically Selected"
            body="<p>Visit type is automatically selected based on the existence of a previous encounter in the last 36 months for this patient and practice type.</p>"
          ></help-icon>
        </radio-buttons>
        <radio-buttons
          v-model="encounter.visit_location"
          :items="visitLocations"
          label="Visit Location"
          columns="4"
          required
        ></radio-buttons>
        <!-- Start: TXP-136 hide until later release -->
        <checkbox
          v-if="encounter.visit_type !== 'New' && !isQuickEntry"
          v-model="encounter.is_deferred_care"
          label="Defer patient care?"
          class="mb-2"
          hide-details
          disable-label-click
        ></checkbox>

        <div
          v-if="encounter.is_deferred_care"
          class="mb-6 pa-4 rounded"
          style="border: 1px solid #ccc;"
        >
          <v-row no-gutters align="start" class="mb-3">
            <v-col cols="auto">
              <v-checkbox
                v-model="encounter.is_deferred_care_send_to_fac"
                :ripple="false"
                hide-details
                class="mt-0"
              />
            </v-col>
            <v-col>
              <p class="mb-0">
                <strong>Deliver copy of note to facility?</strong><br>
                <small>
                  If left unchecked, note will not be delivered to the facility. Only check this box if the facility requires a copy of this note.
                </small>
              </p>
            </v-col>
          </v-row>

          <text-area
            v-if="!isQuickEntry"
            v-model="encounter.deferred_care_reason"
            label="Enter reason patient care was deferred..."
            class="col-sm-12"
            required
            counter="1024"
            maxlength="1024"
          ></text-area>
        </div>
        <!-- End: TXP-136 -->
      </v-form>
    </v-card-text>
    <v-card-actions>
      <!-- cancel -->
      <btn
        label="Cancel"
        color="secondary"
        :icon="icons.mdiCancel"
        @click="cancelEncounter"
      ></btn>

      <v-spacer></v-spacer>

      <!-- create quick entry -->
      <btn
        v-if="isQuickEntry"
        :disabled="!formValid"
        label="Create Quick Entry"
        :icon="icons.mdiClockPlus"
        @click="createEncounter"
      ></btn>

      <!-- create deferred care encounter -->
      <btn
        v-else-if="encounter.is_deferred_care"
        :disabled="!formValid"
        label="Create Deferred Care Encounter"
        :icon="icons.mdiAccountOff"
        @click="createEncounter"
      ></btn>

      <!-- create encounter -->
      <btn
        v-else
        :disabled="!formValid"
        label="Create Encounter"
        :icon="icons.mdiAccountMultiplePlus"
        @click="createEncounter"
      ></btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import '@/components/patients/primitives'
import EncounterCreation from '@/mixins/EncounterCreation'
import { isDateOfServicePriorToDob } from '@/utils/encounterHelpers'
import {
  mdiAccountMultiplePlus,
  mdiAccountOff,
  mdiCancel,
  mdiClockPlus,
  mdiClose,
  mdiInformationOutline,
} from '@mdi/js'
import dayjs from 'dayjs'
import { mapGetters } from 'vuex'
import PatientInsuranceBanner from '@/components/elements/PatientInsuranceBanner.vue'

export default {
  components: { PatientInsuranceBanner },
  mixins: [EncounterCreation],
  data() {
    return {
      chip: true,
      formValid: false,
      dos_ack: 0,
      patient: {},
      icons: {
        mdiAccountMultiplePlus,
        mdiCancel,
        mdiClose,
        mdiClockPlus,
        mdiAccountOff,
        mdiInformationOutline,
      },
    }
  },
  computed: {
    ...mapGetters('route', ['lastRoute']),
    ...mapGetters('auth', ['facilities']),
    ...mapGetters('baseData', ['practiceTypeTemplates']),
    ...mapGetters('encounters', ['baseDataLoaded', 'visitLocations', 'visitTypes']),
    isQuickEntry() {
      return this.$route.query
        && String(this.$route.query.quickEntry).toLowerCase() === 'true'
        && this.encounter.practice_type_id === 1
    },
    title() {
      return this.isQuickEntry ? 'Create Quick Entry' : 'Create Encounter'
    },
    getMinDate() {
      if (this.isQuickEntry) return this.$date().format('YYYY-MM-DD')

      return this.$date().subtract(1, 'year').format('YYYY-MM-DD')
    },
    previousEncounter() {
      return this.$store.getters['encounters/previousEncounter'](this.patient.id, this.encounter.practice_type_id)
    },
  },
  watch: {
    'encounter.visit_date': {
      handler(date, newDate) {
        const todayDate = new Date()

        todayDate.setHours(0, 0, 0, 0)

        const inputDate = new Date(newDate)

        // Check if visit date is prior to patient's date of birth
        if (isDateOfServicePriorToDob(date, this.patient.dob)) {
          this.$store.dispatch('notify', {
            value: "The visit date cannot be prior to the patient's date of birth. Please select a valid date.",
            color: 'error'
          })
          this.encounter.visit_date = this.$date().format('YYYY-MM-DD')
          return
        }

        if (inputDate < todayDate && this.isQuickEntry) {
          this.$store.dispatch('notify', { value: "The quick entry visit date must be set to today's date.", color: 'error' })
          this.encounter.visit_date = this.$date().format('YYYY-MM-DD')
        }
      },
    },
    'encounter.is_deferred_care'(newVal) {
      if (!newVal) {
        this.resetDeferredCareFields()
      }
    },

    'encounter.practice_type_id'(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.resetDeferredCare()
      }
    },
  },
  mounted() {
    if (this.patient.is_deceased) {
      this.$store.dispatch('notify', { value: 'Can\'t create encounter (patient deceased).', color: 'error' })
      this.$router.push({ name: 'encounter-patient-find' })
    }

    if (!this.$route.query.id) {
      this.$store.dispatch('notify', { value: 'Search for patient to create an encounter note.' })
      this.$router.push({ name: 'encounter-patient-find' })
    } else {
      this.patient = { ...this.$store.getters['patients/getById'](this.$route.query.id) }
    }
    this.primeEncounter()
    this.selectVisitType(this.previousEncounter)

    // If coming from the Quick Entry page, create the Quick Entry record automatically
    if (this.isQuickEntry && this.formValid && this.lastRoute.name === 'quick-entry') {
      this.createEncounter()
    }
  },
  methods: {
    resetDeferredCareFields() {
      this.encounter.is_deferred_care_send_to_fac = false
      this.encounter.deferred_care_reason = null
    },

    resetDeferredCare() {
      this.encounter.is_deferred_care = false
      this.resetDeferredCareFields()
    },
    formatDate(dateTime) {
      return this.$date(dateTime).format('M/D/YYYY')
    },
    createEncounter() {
      // Used for follow-up visit and confirmVisitDate method
      // const previousEncounter = this.$store.getters['encounters/previousEncounter'](this.patient.id, this.encounter.practice_type_id, this.encounter.visit_date)

      // Inform User if pervious visit date is more than 7 days before current visit date
      if (this.previousEncounter && !this.dos_ack) {
        if (this.confirmVisitDate(this.previousEncounter.visit_date)) {
          const noteDialog = `The entered date of service is over a week beyond the patient's previous visit date. Please double check that there is not another visit to enter between ${this.formatDate(this.previousEncounter.visit_date)} and ${this.formatDate(this.encounter.visit_date)}.`
          this.$root.confirm({
            title: 'Notice: Date of Service',
            body: noteDialog,
            large: true,
            cancel: false,
          })

          // Ensures dialogue doesn't appear twice for user
          this.dos_ack = 1
          this.$root.previousPath = this.$route.path

          return
        }
      }

      // Add some required values to the encounter object
      this.encounter.patient_id = this.patient.id
      this.encounter.is_hospice = this.previousEncounter.is_hospice

      const userId = this.$scribeProvider.id ?? this.$authUser.id()
      // Check to see if it's a duplicate encounter
      const isDuplicate = this.$store.getters['encounters/isDuplicateEncounter'](this.encounter, userId)

      // Verify we're not trying to create an encounter prior to another encounter
      const isPrior = this.$store.getters['encounters/isPriorEncounter'](this.encounter)

      if (isDuplicate && this.encounter.is_quick_entry) {
        const duplicateEncounterData = this.$store.getters['encounters/getById'](isDuplicate)

        const { is_quick_entry, is_deferred_care, id } = duplicateEncounterData

        let body = ''

        if (is_deferred_care) {
          body = 'A deferred care note was created for this patient for today\'s date. A quick entry cannot be created. Do you want to be redirected to the deferred care encounter?'
        } else if (is_quick_entry) {
          body = 'A quick entry already exists for this patient. Navigate back to the quick entry page?'
        } else {
          body = 'An encounter already exists with this date of service. Do you want to set this encounter as a quick entry and navigate back to the quick entry page?'
        }

        // Practice route
        const practice = this.$store.getters['baseData/practiceTypeFromId'](this.encounter.practice_type_id)

        this.$root.confirm({
          title: 'Duplicate Encounter Found, Redirect?',
          body,
          cancel: 'No',
          confirm: 'Yes',
        }).then(result => {
          if (!result) return

          this.$store.dispatch('loading', true)

          if (is_deferred_care) {
            this.$router.push({
              name: practice.encounterRoute,
              query: { id },
            })
          } else {
            this.navigateToQuickEntry(isDuplicate, true, duplicateEncounterData)
          }
        })
      } else if (isDuplicate && !this.encounter.is_quick_entry) {
        this.$root.confirm({
          title: 'Duplicate Encounter Found, Redirect?',
          body: 'A duplicate encounter was found, do you wish to be redirected to the encounter?',
          cancel: 'No',
          confirm: 'Yes',
        }).then(result => {
          if (result) {
            this.$store.dispatch('loading', true)

            this.$router.push({
              name: this.$store.getters['baseData/practiceTypeFromId'](this.encounter.practice_type_id).encounterRoute,
              query: { id: isDuplicate },
            })
          }
        })
      } else if (isPrior) {
        this.$root.confirm({
          titleIconColor: 'error',
          title: 'Invalid visit date!',
          body: 'You can\'t create an encounter with a visit date prior to another encounter. Encounters must occur in chronological order.',
          cancel: false,
        })
      } else {
        this.generateEncounter(this.previousEncounter)
          .then(() => {
            this.navigateToPage()
          })
      }
    },
    cancelEncounter() {
      if (this.formValid) {
        this.$root.confirm({
          title: 'Cancel?',
          body: 'Are you sure you wish to cancel creating an encounter?',
          cancel: 'No',
          confirm: 'Yes',
        }).then(result => {
          if (result) {
            this.$store.dispatch('route/back')
          }
        })
      } else {
        this.$store.dispatch('route/back')
      }
    },
    navigateToPage() {
      if (this.isQuickEntry) {
        // If quick entry, route to quick entry listing
        this.$store.dispatch('notify', { value: 'Quick entry created successfully.', color: 'success' })
        this.$router.push({
          name: 'quick-entry',
          query: {
            facility: this.$route.params.placeOfServiceId,
            visit_date: this.encounter.visit_date,
          },
        })
      } else if (this.encounter.is_deferred_care) {
        // If deferred care, go to the encounter note
        this.$router.push({
          name: this.$store.getters['baseData/practiceTypeFromId'](this.encounter.practice_type_id).noteRoute,
          query: { id: this.encounter.id },
        })
      } else {
        // Route to the new encounter
        this.$store.dispatch('notify', { value: 'Encounter created successfully.', color: 'success' })
        this.$store.dispatch('loading', true)
        this.$router.push({
          name: this.$store.getters['baseData/practiceTypeFromId'](this.encounter.practice_type_id).encounterRoute,
          query: { id: this.encounter.id },
        })
      }
    },
    confirmVisitDate(prevDate) {
      const currDos = dayjs(this.encounter.visit_date).startOf('day')

      // Add a week to the previous date of service
      const weekAfterPrevDos = dayjs(prevDate).startOf('day').add(1, 'week')

      // Notify the user if the selected date is more than 1 week after the prior visit
      return currDos.isAfter(weekAfterPrevDos, 'day')
    },
    navigateToQuickEntry(duplicateId, markAsQuickEntry, duplicateEncounterData) {
      if (markAsQuickEntry) {
        // If we are creating a QE and found a non QE duplicate encounter, we set the flag is_quick_entry to true
        // So it will show up on the QE List

        this.$store.commit('encounters/updateEncounter', { encounter: { ...duplicateEncounterData, is_quick_entry: true } })
      }

      this.$router.push({
        name: 'quick-entry',
        query: { id: duplicateId },
      })
    },
  },
}
</script>
<style lang="scss">
  .chip {
    word-wrap: break-word;
    word-break: break-word;
    white-space: normal;
    min-height: fit-content;
    padding: 10px; /* Adjust padding as needed */
    box-sizing: border-box; /* Ensure padding and width work as expected */
    justify-content: space-around;
  }

</style>
